import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
  signal,
  input as signalInput,
  ViewChild,
} from '@angular/core';
import {
  AutoCompleteOption,
  GcSnackbarService,
  IpoTranslatePipe,
} from '@ip-only/front-end-library';
import { CircleMarker, Control, ControlPosition, Layer, Map } from 'leaflet';
import {
  RP_OPEN_ORDERS_OVERVIEW_MODAL,
  RP_ORDERS_OVERVIEW_OPENED,
} from '../../../shared/posthug-analytics/analytics-event-types';
import { AnalyticsService } from '../../../shared/posthug-analytics/analytics.service';
import { disableMapInteractionsOnElement } from '../../../shared/util/disable-map-interactions-helper';
import { ScreenSizeService } from '../../../shared/util/screen-size/screen-size.service';
import { PointService } from '../../service/map/point/point.service';
import { SelectSpanModeService } from '../../service/map/span/select-span-mode/select-span-mode.service';
import {
  DistPointLayerTypeMap,
  DistributionPointProperties,
  MapIconsStyles,
  SelectedMapItem,
} from '../../span-progress-entities';
import { ReportModalService } from '../../span-progress-report/report-modal.service';
import { MapInteractionService } from '../map-interaction/map-interaction.service';
import { IconData, legendDataV2 } from './legend';

export interface CustomControl {
  position: ControlPosition;
  domElement: HTMLElement;
}

interface ControlDefinition {
  position: ControlPosition;
  elementRef: ElementRef<any>;
}

export type SidePanel = 'ducts' | 'reports';

@Component({
  selector: 'app-map-controls',
  templateUrl: './map-controls.component.html',
  styleUrls: ['./map-controls.component.scss'],
  standalone: false,
})
export class MapControlsComponent implements AfterViewInit, OnInit {
  private readonly selectSpanModeService = inject(SelectSpanModeService);
  @Input() map: Map;
  @Input() showGeolocation = false;
  @Input() overlay: Record<string, Layer> = {};
  @Input() activeSidePanel: SidePanel | null = null;

  readonly version = signalInput<string>('');

  @Output() geolocationClicked = new EventEmitter();
  @Output() homeClicked = new EventEmitter();
  @Output() sidePanelSelected = new EventEmitter<SidePanel>();

  @ViewChild('versionControl', { static: false }) versionControl: ElementRef;
  @ViewChild('homeControl', { static: false }) homeControl: ElementRef;
  @ViewChild('locationControl', { static: false }) locationControl: ElementRef;
  @ViewChild('ordersControl', { static: false }) ordersControl: ElementRef;
  @ViewChild('ductMenuControl', { static: false }) ductMenuControl: ElementRef;
  @ViewChild('reportsPanelControl', { static: false })
  reportsPanelControl: ElementRef;
  @ViewChild('legendControl', { static: false }) legendControl: ElementRef;
  @ViewChild('searchControl', { static: false }) searchControl: ElementRef;
  @ViewChild('filterMenuControl', { static: false })
  filterMenuControl: ElementRef;
  @ViewChild('selectedResultControl', { static: false })
  selectedResultControl: ElementRef;

  controlList: ControlDefinition[] = [];
  controls: Control[] = [];
  isSelectSpanMode = this.selectSpanModeService.isSelectSpanMode;
  isAfterViewInit = false;

  legendData = legendDataV2;
  overlayEntries: { key: string; layer: Layer }[] = [];

  legendToggled = false;
  filterOpened = false;
  openSnackBar = false;

  isMobileScreen = signal(false);
  showFullVersionOnMobile = signal(false);

  searchOptions: AutoCompleteOption[] = [];
  resultData: Layer[] = [];
  selectedResultControl$ =
    this.mapInteractionService.getSelectedResultControl$();

  constructor(
    private screenSizeService: ScreenSizeService,
    private cdr: ChangeDetectorRef,
    private pointService: PointService,
    private mapInteractionService: MapInteractionService,
    private analyticsService: AnalyticsService,
    private reportModalService: ReportModalService,
    private snackbar: GcSnackbarService,
    private translate: IpoTranslatePipe,
  ) {
    this.isMobileScreen.set(this.screenSizeService.checkIsMobileScreen());
    effect(() => {
      if (this.isSelectSpanMode()) {
        this.removeAllControls(this.map);
        return;
      }
      if (this.isAfterViewInit) {
        this.createControls(this.map);
      }
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.setIsMobileScreen();
  }

  ngOnInit(): void {
    this.overlayEntries = Object.entries(this.overlay).map(([key, layer]) => ({
      key,
      layer,
    }));

    this.setIsMobileScreen();
  }

  readonly hasVersion = computed(() => !!this.version()?.trim());

  versionButtonText = computed(() => {
    const version = this.version() ?? '';

    if (!this.isMobileScreen()) {
      return version;
    }

    return this.showFullVersionOnMobile() ? version : 'Vers.';
  });

  isLayerAdded(layer: L.Layer): boolean {
    return this.map.hasLayer(layer);
  }

  toggleLayer(checked: boolean, layer: L.Layer) {
    if (checked) {
      this.map.addLayer(layer);
    } else {
      this.map.removeLayer(layer);
    }
  }

  ngAfterViewInit() {
    if (this.map) {
      this.setControlList();
      this.createControls(this.map);
      this.isAfterViewInit = true;
    }
  }

  setIsMobileScreen = () => {
    const wasMobile = this.isMobileScreen();
    const isMobile = this.screenSizeService.checkIsMobileScreen();
    this.isMobileScreen.set(isMobile);

    if (!wasMobile && isMobile) {
      this.showFullVersionOnMobile.set(false);
    }

    this.cdr.detectChanges();
  };

  private readonly setControlList = () => {
    this.controlList = [
      {
        position: this.isMobileScreen() ? 'topright' : 'topleft',
        elementRef: this.searchControl,
      },
      { position: 'topleft', elementRef: this.selectedResultControl },
      { position: 'bottomleft', elementRef: this.locationControl },
      { position: 'bottomleft', elementRef: this.homeControl },
      { position: 'bottomright', elementRef: this.versionControl },
      { position: 'topright', elementRef: this.ordersControl },
      { position: 'topright', elementRef: this.ductMenuControl },
      { position: 'topright', elementRef: this.reportsPanelControl },
      { position: 'topright', elementRef: this.legendControl },
      { position: 'topright', elementRef: this.filterMenuControl },
    ];
  };

  private createCustomControl(_control: CustomControl) {
    return new (Control.extend({
      options: {
        position: _control.position,
      },
      onAdd: () => {
        return _control.domElement;
      },
    }))();
  }

  addCustomControl = (controlDefinition: ControlDefinition, map: Map) => {
    const element = disableMapInteractionsOnElement(
      controlDefinition.elementRef.nativeElement,
    );
    const customControl = this.createCustomControl({
      position: controlDefinition.position,
      domElement: element,
    });

    this.controls.push(customControl);
    map.addControl(customControl);
  };

  createControls(map: Map) {
    this.controls.push(
      new Control.Scale({
        maxWidth: 100,
        metric: true,
        imperial: false,
      }),
    );

    map.addControl(this.controls[this.controls.length - 1]);

    this.controlList.forEach((controlDef) =>
      this.addCustomControl(controlDef, map),
    );

    this.addClassToVersionControl();
    this.isMobileScreen()
      ? map.zoomControl.remove()
      : map.zoomControl.setPosition('bottomright');
  }

  removeAllControls(map: Map) {
    this.controls.forEach((c) => {
      map.removeControl(c);
    });
    this.controls = [];
  }

  openOrdersOverviewModal(e: MouseEvent | Event) {
    e.stopPropagation();
    this.reportModalService.setModalState({
      ordersOverviewModal: { open: true },
    });
    this.analyticsService.logEvent(RP_OPEN_ORDERS_OVERVIEW_MODAL);
    this.analyticsService.logEvent(RP_ORDERS_OVERVIEW_OPENED);
  }

  onLegendClicked(e: MouseEvent | Event): void {
    e.stopPropagation();
    this.legendToggled = !this.legendToggled;
  }

  onFilterClicked(e: MouseEvent | Event): void {
    e.stopPropagation();
    this.filterOpened = !this.filterOpened;
  }

  onDuctMenuClicked(e: MouseEvent | Event): void {
    e.stopPropagation();
    this.sidePanelSelected.emit('ducts');
  }

  onReportsPanelClicked(e: MouseEvent | Event): void {
    e.stopPropagation();
    this.sidePanelSelected.emit('reports');
  }

  addAlpha(color: string) {
    return color + '33';
  }

  buildSearchOptions(input: string) {
    const layerData = this.pointService.getLayerData();
    this.resultData = [];
    Object.keys(DistPointLayerTypeMap).forEach((key) => {
      if (layerData[key]) {
        const result = layerData[key]
          .getLayers()
          .filter(
            (point: CircleMarker) =>
              (point.feature?.properties as DistributionPointProperties).name
                .toLowerCase()
                .includes(input.toLowerCase()) ||
              (
                point.feature?.properties as DistributionPointProperties
              ).telematorId
                .toLowerCase()
                .includes(input.toLowerCase()),
          );
        this.resultData = [...this.resultData, ...result];
      }
    });
    this.resultData.forEach((data: CircleMarker) => {
      this.searchOptions.push({
        label: (data.feature?.properties as DistributionPointProperties)
          .telematorId
          ? `${
              (data.feature?.properties as DistributionPointProperties)
                .telematorId
            } (${
              (data.feature?.properties as DistributionPointProperties).name
            })`
          : (data.feature?.properties as DistributionPointProperties).name,
        value: (data.feature?.properties as DistributionPointProperties).id,
      });
    });
  }

  onSelectedSearchFeature(option: AutoCompleteOption<any>) {
    this.mapInteractionService.setSelectedResultControl$({} as SelectedMapItem);

    const selectedLayer = this.resultData.find(
      (circleMarker: CircleMarker) =>
        (circleMarker.feature?.properties as DistributionPointProperties).id ===
        option.value,
    );
    if (selectedLayer) {
      this.mapInteractionService.onSelectSearchedItem(
        selectedLayer as CircleMarker,
      );
    }
  }

  onSearchFeature(event: Event) {
    this.mapInteractionService.setSelectedResultControl$({} as SelectedMapItem);
    this.searchOptions = [];
    const searchTerm = (event.target as HTMLInputElement).value;
    if (searchTerm.length >= 3) {
      this.buildSearchOptions(searchTerm);
    }
  }

  shouldClearResultControl(event: Event) {
    if ((event.target as HTMLInputElement).value) {
      this.mapInteractionService.setSelectedResultControl$(
        {} as SelectedMapItem,
      );
      this.mapInteractionService.blurFeatureSelection('mapInteraction');
    }
  }

  calculatePointBorderRadius(icon: IconData) {
    if (icon.isIcon && icon.iconStyle) {
      return this.calculatePointIconBorderRadius(icon.iconStyle);
    } else {
      return '50%';
    }
  }

  calculatePointIconBorderRadius(iconStyle: MapIconsStyles) {
    switch (iconStyle) {
      case MapIconsStyles.SQUARE:
        return 0;
      case MapIconsStyles.SHEILD:
        return '0 0 50% 50%';
      case MapIconsStyles.RHOMBUS:
        return 0;
      default:
        return 0;
    }
  }

  calculatePointTransform(icon: IconData) {
    if (icon) {
      return icon.iconStyle === MapIconsStyles.RHOMBUS
        ? 'rotate(45deg)'
        : 'none';
    } else {
      return 'none';
    }
  }

  addClassToVersionControl() {
    (this.versionControl.nativeElement as HTMLElement).classList.add(
      'custom-version-control',
    );
  }

  onVersionClicked(event: MouseEvent) {
    event.stopPropagation();

    if (this.isMobileScreen()) {
      this.showFullVersionOnMobile.set(true);
    }

    this.openVersionSnackbar();
    this.cdr.detectChanges();
  }

  openVersionSnackbar() {
    this.snackbar.addSnackbar({
      message: this.translate.transform('constructionExplanation'),
      variant: 'primary',
      closeButton: 'OK',
      autoHideDuration: 20000,
    });
  }

  allOverlaysChecked(): boolean {
    return this.overlayEntries.every((entry) => this.isLayerAdded(entry.layer));
  }

  selectAllLayers(checked: boolean) {
    this.overlayEntries.forEach((entry) => {
      if (checked) {
        this.map.addLayer(entry.layer);
      } else {
        this.map.removeLayer(entry.layer);
      }
    });
  }
}
